<!--
	@name app-firm-tab-view-trading-styles
	@description Firm Trading Styles
	@date 2020/08/26
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<div :component="$options.name">
		<common-structure-section>
			<template #body>
				<v-fade-transition hide-on-leave>
					<div v-if="!organisationTradingStyleList">
						<p class="text-body-1 mb-3 text-center">
							Loading trading styles
						</p>
						<v-progress-linear height="6" indeterminate />
					</div>
				</v-fade-transition>
				<v-fade-transition hide-on-leave>
					<div v-if="organisationTradingStyleList">
						<v-row no-gutters class="justify-space-between pb-5">
							<v-col class="align-self-left">
								<v-pagination v-model="currentPage" :length="pageCount" :total-visible="8" color="info" />
							</v-col>
							<v-col class="shrink align-self-center">
								<v-btn v-if="department.name.includes('owner') || department.name.includes('manager')" @click="openTradingStyleDialog">
									Add New Trading Style
								</v-btn>
							</v-col>
						</v-row>
						<div v-if="!organisationTradingStyleList.length">
							<div class="text-body-1 font-weight-bold mb-3">
								Create separate Trading Styles with which to group Accounts.
							</div>
							<div class="text-body-2 mb-3">
								Once a Trading Style has been created, you can assign it to an Account using the Link Trading Style action in the Manage Accounts section. Assigning an
								account to a Trading Style will result in the Trading Style being used in place of the Company details for system generated documentation such as Fact
								Finds.
							</div>
							<div class="text-body-2">
								Click the ADD NEW TRADING STYLE button to get started.
							</div>
						</div>
						<v-data-table
							v-else
							class="table--styled elevation-3"
							:footer-props="footerProps"
							:headers="headers"
							:items="orderedTradingStyles"
							:items-per-page="itemsPerPage"
							:page.sync="currentPage"
							@page-count="pageCount = $event"
						>
							<template #body="{ items }">
								<tbody>
									<tr v-for="item in items" :key="item.id" class="tbl-row">
										<td>
											<p class="text-body-1 font-weight-bold mb-0">
												{{ item.name }}
											</p>
										</td>
										<td v-if="organisation.data?.tradingStyles?.editFCA" class="nowrap">
											{{ item.data?.fca || '--' }}
										</td>
										<td v-if="organisation.data?.tradingStyles?.editFCA" class="nowrap">
											<v-chip light label small :color="isTradingStyleApproved(item) ? 'rgba(23, 184, 144, .25)' : 'rgba(103, 0, 86, 0.25)'">
												{{ isTradingStyleApproved(item) ? 'Approved' : 'Pending' }}
											</v-chip>
										</td>
										<td class="nowrap">
											{{ item.created | moment('Do MMM YYYY, HH:mm') }}
										</td>

										<td v-if="department.name.includes('owner') || department.name.includes('manager')" class="text-right shrunk">
											<v-tooltip bottom content-class="text-center" max-width="250">
												<template #activator="{ on }">
													<v-btn color="primary" icon :loading="loading[item.id]" @click="openEditTradingStyleDialog(item)" v-on="on">
														<v-icon>mdi-pencil</v-icon>
													</v-btn>
												</template>
												<span> Edit trading style name </span> <span v-if="organisation?.data?.tradingStyles?.editFCA"> and FCA</span>
											</v-tooltip>
											<v-tooltip v-if="organisation?.data?.panels?.active" bottom content-class="text-center" max-width="250">
												<template #activator="{ on }">
													<v-btn color="secondary" icon class="ml-2" @click="openAddEditPanelDialog(item)" v-on="on">
														<v-icon>mdi-card-text-outline</v-icon>
													</v-btn>
												</template>
												<span> Select panel for this trading style </span>
											</v-tooltip>
											<v-tooltip bottom content-class="text-center" max-width="250">
												<template #activator="{ on }">
													<v-btn color="error" icon :loading="loading[item.id]" class="ml-2" @click="deleteTradingStyle(item)" v-on="on">
														<v-icon>mdi-trash-can</v-icon>
													</v-btn>
												</template>
												<span> Delete this trading style </span>
											</v-tooltip>
										</td>
									</tr>
								</tbody>
							</template>
						</v-data-table>
					</div>
				</v-fade-transition>
			</template>
		</common-structure-section>

		<common-dialog-confirm ref="confirm" />
		<app-firm-dialog-add-trading-style ref="addTradingStyleDialog" :form="form('firm_trading_styles').value" />
		<app-firm-dialog-edit-trading-style ref="editTradingStyleDialog" :form="form('firm_trading_styles').value" />
		<app-firm-dialog-add-edit-panel ref="addEditPanelDialog" />
		<app-firm-dialog-add-providers ref="providersDialog" :organisations="organisations" @submitted="loadOrganisationTradingStyleList" />
		<app-firm-dialog-restriction ref="restrictionDialog" @submitted="loadOrganisationTradingStyleList" />
	</div>
</template>

<script>
	import { ElementTools } from '@/utils';
	import { orderBy } from 'lodash';
	import { mapActions, mapGetters, mapState } from 'vuex';

	import CommonDialogConfirm from '@/component/common/dialog/confirm';
	import CommonStructureSection from '@/component/common/structure/section';
	import AppFirmDialogAddEditPanel from '../dialog/add-edit-panel';
	import AppFirmDialogAddProviders from '../dialog/add-providers';
	import AppFirmDialogAddTradingStyle from '../dialog/add-trading-style';
	import AppFirmDialogEditTradingStyle from '../dialog/edit-trading-style';
	import AppFirmDialogRestriction from '../dialog/restriction';

	export default {
		name: 'app-firm-members-list',

		components: { CommonStructureSection, CommonDialogConfirm, AppFirmDialogAddTradingStyle, AppFirmDialogEditTradingStyle, AppFirmDialogAddProviders, AppFirmDialogRestriction, AppFirmDialogAddEditPanel },

		props: {},

		data() {
			return {
				ots: [],
				loading: {},
				formData: {},
				currentPage: 1,
				itemsPerPage: 10,
				pageCount: 0,
				organisations: [],
				selectedProviders: [],
				organisationUserList: [],
				footerProps: {
					'items-per-page-options': [5, 10, 15, 20, 25],
					showFirstLastPage: true,
					firstIcon: 'mdi-arrow-collapse-left',
					lastIcon: 'mdi-arrow-collapse-right',
					prevIcon: 'mdi-minus',
					nextIcon: 'mdi-plus'
				}
			};
		},

		computed: {
			...mapState('Account', ['user', 'organisation', 'department']),
			...mapState('AccountOrganisationTradingStyle', ['organisationTradingStyleList']),
			...mapGetters('CmsForm', ['form']),

			headers() {
				let headers = [
					{ text: 'Name', align: 'left', value: 'name', class: 'text-no-wrap', sortable: false }
				];

				if (this.organisation.data?.tradingStyles?.editFCA) {
					headers.push({ text: 'FCA No.', align: 'left', value: 'data.fca', class: 'text-no-wrap', sortable: false });
				}
				if (this.organisation.data?.tradingStyles?.editFCA) {
					headers.push({ text: 'Status', align: 'left' });
				}

				headers.push({ text: 'Created', align: 'left', value: 'created', class: 'text-no-wrap', sortable: false  });
				headers.push({ text: '', align: 'centre' });

				return headers;
			},

			orderedTradingStyles() {
				return orderBy(this.organisationTradingStyleList, 'created', ['desc']) || [];
			},

			providerOptions() {
				return this.organisations
					?.filter((o) => !this.selectedProviders.includes(o.id))
					?.map((o) => ({
						text: o.name,
						value: o.id
					}));
			}
		},

		created() {
			this.loadOrganisationTradingStyleList();
			this.fetchOrganisationList();
			this.fetchOrganisationUserList();
			this.loadForm('firmTradingStyles');
		},

		methods: {
			...mapActions('AccountOrganisation', ['loadProviderList']),
			...mapActions('AccountOrganisationTradingStyle', ['loadOrganisationTradingStyleList', 'deleteOrganisationTradingStyle']),
			...mapActions('AccountOrganisationUser', ['loadOrganisationUserList']),
			...mapActions('CmsForm', ['loadForm']),

			fetchOrganisationList() {
				this.loadProviderList({ /* where: { id: { value: organisationIds, type: 'uuid' } },*/ order: { property: 'organisation.name' } }).then(({ data }) => {
					this.organisations = data;
				});
			},

			fetchOrganisationUserList() {
				this.loadOrganisationUserList(this.organisation.id).then(({ data }) => {
					this.organisationUserList = data;
				});
			},

			/**
			 * @name openTradingStyleDialog
			 * @description Open add trading style dialog
			 */
			openTradingStyleDialog() {
				this.$refs.addTradingStyleDialog.open();
			},

			/**
			 * @name openEditTradingStyleDialog
			 * @description Open add trading style dialog
			 * @param {Object} item
			 */
			openEditTradingStyleDialog(item) {
				this.$refs.editTradingStyleDialog.open(item);
			},

			/**
			 * @name openAddEditPanelDialog
			 * @description Open add/edit panel dialog
			 * @param {Object} item
			 */
			 openAddEditPanelDialog(item) {
				this.$refs.addEditPanelDialog.open(item);
			},

			/**
			 * @name openProvidersDialog
			 * @description Event for provider panel dialog
			 */
			openProvidersDialog(item) {
				this.$refs.providersDialog.open(item);
			},

			/**
			 * @name openRestrictionDialog
			 * @description Event for product restriction dialog
			 */
			 openRestrictionDialog(item, id) {
				this.$refs.restrictionDialog.open(item, id);
			},

			/**
			 * @name deleteTradingStyle
			 * @description Delete trading style
			 * @param {Object} item
			 */
			async deleteTradingStyle(item) {
				if (this.userByOrganisationTradingStyleId(item.id))
					ElementTools.fireNotification(
						this.$el,
						'error',
						`${item.name} cannot be removed since there are accounts associated to it. Please unlink all accounts from this trading style before attempting to delete it`
					);
				else {
					this.$refs.confirm.open('Delete Trading Style', `Are you sure you wish to delete ${item.name}?`).then(async() => {
						this.$set(this.loading, item.id, true);

						this.deleteOrganisationTradingStyle(item.id)
							.then(() => ElementTools.fireNotification(this.$el, 'success', `${item.name} has been successfully deleted`))
							.catch(() => ElementTools.fireNotification(this.$el, 'error', 'Failed to delete trading style'))
							.finally(() => this.$set(this.loading, item.id, false));
					});
				}
			},

			/**
			 * @name userByOrganisationTradingStyleId
			 * @description Get user by organisation trading style id
			 * @param {Object} item
			 */
			userByOrganisationTradingStyleId(id) {
				return this.organisationUserList.find((u) => u.organisationTradingStyle && u.organisationTradingStyle.id === id);
			},

			/**
			 * @name isTradingStyleApproved
			 * @description Check if trading style is approved
			 * @param {Object} item
			 */
			isTradingStyleApproved(item) {
				if (item.data?.fca && !item.approved) {
					return false;
				}
				return true;
			}
		}
	};
</script>

<style lang="scss" scoped>
	::v-deep .v-pagination {
		justify-content: start !important;
	}
</style>
